import { forwardRef, useState } from "react";
import { Eye, EyeOff, Copy, Check, RefreshCw } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * ApiKeyDisplay — masked API key field (Faza 8 polish).
 *
 * Features:
 *   - Toggle show/hide (Eye/EyeOff)
 *   - Copy button (clipboard) — feedback bilan
 *   - Optional regenerate (Generate new) action
 *   - Masking: -webkit-text-security CSS yoki manual `••••...••••` last 4
 *   - lastUsedAt — meta footer
 *
 * UX:
 *   - Yopiq holatda ko'rsatadi: "sk_live_••••••••••••a3f2" (last 4)
 *   - Copy bosilganda 2s "Nusxalandi" feedback
 *
 * Token'lar: --api-key-* (tokens.css)
 */
export interface ApiKeyDisplayProps {
  /** Real key qiymat (clipboard'ga shu nusxalanadi) */
  value: string;
  /** Boshlang'ichda ko'rsatish (default false — yashirin) */
  defaultRevealed?: boolean;
  /** Last 4 character'ni mask ostida ham ko'rsatish (default true) */
  showSuffix?: boolean;
  /** Suffix uzunligi (default 4) */
  suffixLength?: number;
  /** Prefix (key tipi: "sk_live_", "pk_") — har doim ko'rinadi */
  prefix?: string;
  /** "Yangidan yaratish" bosilganda */
  onRegenerate?: () => void;
  /** Regenerate jarayoni davom etmoqdami */
  regenerating?: boolean;
  /** So'nggi marta ishlatilgan vaqt (formatted) */
  lastUsedAt?: string;
  /** Read-only label (yon-yon) */
  label?: string;
  className?: string;
}

export const ApiKeyDisplay = forwardRef<HTMLDivElement, ApiKeyDisplayProps>(
  (
    {
      value,
      defaultRevealed = false,
      showSuffix = true,
      suffixLength = 4,
      prefix,
      onRegenerate,
      regenerating = false,
      lastUsedAt,
      label,
      className,
    },
    ref,
  ) => {
    const [revealed, setRevealed] = useState(defaultRevealed);
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch {
        // Silent — caller toast handle
      }
    };

    const masked = (() => {
      if (!showSuffix) return "••••••••••••••••••••";
      const suffix = value.slice(-suffixLength);
      const dots = "•".repeat(Math.max(8, value.length - suffixLength - (prefix?.length ?? 0)));
      return `${dots}${suffix}`;
    })();

    return (
      <div ref={ref} className={cn("flex flex-col gap-1.5", className)}>
        {label && (
          <label className="text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
            {label}
          </label>
        )}
        <div
          className={cn(
            "flex items-center gap-1 rounded-lg border px-2",
            "bg-[var(--api-key-bg)] border-[var(--api-key-border)]",
            "h-10 transition-colors duration-150",
            "focus-within:border-[var(--form-field-border-focus)] focus-within:ring-2 focus-within:ring-[var(--form-field-ring-focus)]",
          )}
        >
          {prefix && (
            <span className="text-xs font-mono text-[var(--text-muted)] select-none">
              {prefix}
            </span>
          )}
          <span
            className={cn(
              "flex-1 text-sm font-mono truncate select-all",
              revealed ? "text-[var(--api-key-fg)]" : "text-[var(--api-key-mask-fg)] tracking-wide",
            )}
            aria-label={revealed ? "API kalit ochiq" : "API kalit yashirin"}
          >
            {revealed ? value : masked}
          </span>

          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-md",
              "text-[var(--api-key-action-fg)] hover:text-[var(--api-key-action-fg-hover)]",
              "hover:bg-[var(--api-key-action-bg-hover)]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/40",
              "transition-colors duration-150",
            )}
            aria-label={revealed ? "Yashirish" : "Ko'rsatish"}
            aria-pressed={revealed}
          >
            {revealed ? (
              <EyeOff className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Eye className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>

          <button
            type="button"
            onClick={handleCopy}
            className={cn(
              "inline-flex h-7 w-7 items-center justify-center rounded-md",
              "text-[var(--api-key-action-fg)] hover:text-[var(--api-key-action-fg-hover)]",
              "hover:bg-[var(--api-key-action-bg-hover)]",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/40",
              "transition-colors duration-150",
              copied && "text-[var(--accent-emerald-fg)]",
            )}
            aria-label="Nusxalash"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>

          {onRegenerate && (
            <button
              type="button"
              onClick={onRegenerate}
              disabled={regenerating}
              className={cn(
                "inline-flex h-7 px-2 items-center gap-1 rounded-md text-xs font-medium",
                "text-[var(--api-key-action-fg)] hover:text-[var(--api-key-action-fg-hover)]",
                "hover:bg-[var(--api-key-action-bg-hover)]",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-violet)]/40",
                "transition-colors duration-150",
                "disabled:opacity-50 disabled:cursor-not-allowed",
              )}
            >
              <RefreshCw
                className={cn("h-3 w-3", regenerating && "animate-spin")}
                aria-hidden
              />
              <span>Yangilash</span>
            </button>
          )}
        </div>
        {(lastUsedAt || copied) && (
          <div className="flex items-center justify-between text-[11px]">
            {copied ? (
              <span className="text-[var(--accent-emerald-fg)]">Nusxalandi</span>
            ) : (
              <span className="text-[var(--text-muted)]">
                {lastUsedAt && `So'nggi foydalanish: ${lastUsedAt}`}
              </span>
            )}
          </div>
        )}
      </div>
    );
  },
);
ApiKeyDisplay.displayName = "ApiKeyDisplay";
